import { LitElement, html, css } from 'lit';
import { EXIST_BASE } from './eplib-constants.js';
import EplibAnimationUtils from './eplib-animation-utils.js';
import { BootstrapCSS } from '../resources/css/bootstrap.css';
import { EplibStyles } from '../resources/css/style.css.js';

/**
 * This component displays text content, by default a page, though it could be a
 * div if division view is selected.
 *
 * @fires eplib-navigate-page - Call ourselves to load first page in document
 * @fires eplib-remove-facsimile - Clear any existing facsimile before loading page
 * @fires eplib-clear-annotations - Reset annotations before loading page
 * @fires eplib-set-annotations - Load annotations associated with page content
 * @fires eplib-content-ready - Let other components know that we have retrieved page content
 * @fires eplib-set-switch-view - Initialize action bar with data needed to switch betwen page and div
 * @fires eplib-set-eebo-link - Initialize action bar with data to provide EEBO link
 * @fires eplib-set-spelling - Tell action bar to initialize standard/original spelling in content
 * @fires eplib-set-word-counts - Tell action bar to show/hide word count rubrics
 * @fires eplib-set-navigation-button-state - Initialize previous/next buttons in action bar
 * @fires eplib-kwic-load-matches - Adds match highlighting to page content after KWIC navigation
 *
 * @listens eplib-enable-disable-annotations - Enables or disables annotatable-document
 * @listens eplib-navigate-page - Navigates the text to a different page (or div)
 * @listens eplib-kwic-load-page - Initiate page load from KWIC result navigation
 */

export class EplibDocumentContent extends LitElement {
  static get properties() {
    return {
      ...super.properties,
      historySupport: {
        type: Boolean
      },
      actionBarComponent: {
        type: HTMLElement
      },
      sliderComponent: {
        type: HTMLElement
      },
      content: {
        type: HTMLElement
      },
      /**
       * indicates we have requested page navigation and are waiting on a response
       */
      loading: {
          type: Boolean
      }
    }
  }

  async firstUpdated() {
    super.firstUpdated();
    this.historySupport = !!(window.history && window.history.pushState);
    const root = this.parentElement.getRootNode(); // should get us eplib-content-view
    if (root) {
      this.actionBarComponent = root.querySelector('eplib-action-bar');
      this.sliderComponent = root.querySelector('eplib-page-slider');
    }
    if (this.actionBarComponent) {
      this.highlighterComponent = this.actionBarComponent.shadowRoot.querySelector('eplib-highlight');
    }

    window.addEventListener('eplib-navigate-page', async (e) => this._loadPage(e));
    if (this.highlighterComponent) {
      this.highlighterComponent.addEventListener('eplib-kwic-load-page', async (e) => this._loadPage(e));
    }
    if (this.actionBarComponent) {
      this.actionBarComponent.addEventListener('eplib-enable-disable-annotations', e => this._enableDisableAnnotations(e));
    }

    const doc = window.location.pathname.split('/').pop();
    const search = document.location.search ? document.location.search.replace(/^\?/, '') : '';
    const url = `doc=${doc}&${search}`;

    const event = new CustomEvent("eplib-navigate-page", {
      detail: {
        url: url,
        direction: 'nav-next'
      },
      bubbles: true,
      composed: true
    });
    this.dispatchEvent(event);

    const annodoc = this.shadowRoot.querySelector('annotatable-document');
    const id = doc.substring(0, doc.lastIndexOf('.'));
    annodoc.setAttribute('source', id);
    annodoc.setAttribute('id', id);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.actionBarComponent) {
      this.actionBarComponent.removeEventListener('eplib-enable-disable-annotations', e => this._enableDisableAnnotations(e));
    }
    if (this.highlighterComponent) {
      this.highlighterComponent.removeEventListener('eplib-kwic-load-page', async (e) => this._loadPage(e));
    }
    window.removeEventListener('eplib-navigate-page', async (e) => this._loadPage(e));
  }

  async _getContent(params) {
    let url = `${EXIST_BASE}/modules/ajax.xql?${params}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'same-origin'
      });
      if (!response.ok) {
        throw new Error(`Network response was not OK: ${response.error}`);
      }
      const data = await response.json();
      if (data.error) {
        throw new Error(`Data error: ${data.error}`);
      }
      return data;
    }
    catch (error) {
      alert(`Error retrieving document content for ${params}: ${error}`);
      return null;
    }
  }

  async _loadPage(e) {
    const params = e.detail.url;
    const direction = e.detail.direction; // facilitates nicer page turn animation

    console.log("Loading %s", params);

    const facsElement = document.querySelector('eplib-facsimile');
    if (facsElement) {
      const facsEvent = new CustomEvent('eplib-remove-facsimile', {});
      facsElement.dispatchEvent(facsEvent);
    }

    const clearAnnoEvent = new CustomEvent('eplib-clear-annotations', { bubbles: true });
    window.dispatchEvent(clearAnnoEvent);

    // prepare parameters to send a query to Blacklab or ajax.xql
    const doc = window.location.pathname.split('/').pop();
    // URLSearchParams does not parse out anything before the ?
    let qparams = new URLSearchParams(params.replace(/^.*\?/, ''));
    const pattern = qparams.get('pattern');
    const matchParam = qparams.get('match');

    // ajax.xql needs the document with extension as a parameter.
    // BlackLab will need it without the extension.
    if (!qparams.has('doc')) {
      qparams.append('doc', doc);
    }

    const easeOut = EplibAnimationUtils._animatePageNav(direction, 'out', this.shadowRoot, '.content', '.loading');

    const data = await this._getContent(qparams);

    if (!data || !data.content) {
      const annoEvent = new CustomEvent('eplib-set-annotations', {
        detail: {
          data: null // clear annotations
        },
        bubbles: true
      });
      window.dispatchEvent(annoEvent);
      if (easeOut) easeOut.complete();
      EplibAnimationUtils._stopLoadingAnimation(this.shadowRoot, '.loading');
      return;
    }

    this.shadowRoot.querySelector("[slot='document']").innerHTML = data.content;
    if (easeOut) easeOut.complete();
    EplibAnimationUtils._animatePageNav(direction, 'in', this.shadowRoot, '.content', '.loading');

    const annoEvent = new CustomEvent('eplib-set-annotations', {
      detail: {
        data: data.annotations
      },
      bubbles: true
    });
    window.dispatchEvent(annoEvent);

    this._initContent();
    this._initTooltip();

    if (this.actionBarComponent) {
      this.content = this.shadowRoot.querySelector('.content');
      if (!this.content) return;
      const readyEvent = new CustomEvent('eplib-content-ready', {
        detail: { content: this.content },
        bubbles: true
      });
      window.dispatchEvent(readyEvent);

      if (data.switchView) {
        const switchEvent = new CustomEvent('eplib-set-switch-view', {
          detail: { switchView: data.switchView }
        });
        this.actionBarComponent.dispatchEvent(switchEvent);
      }

      const linkEvent = new CustomEvent('eplib-set-eebo-link', {
        detail: { eeboLink: data.eeboLink }
      });
      this.actionBarComponent.dispatchEvent(linkEvent);

      const spellEvent = new CustomEvent('eplib-set-spelling', {});
      this.actionBarComponent.dispatchEvent(spellEvent);

      const wordCountEvent = new CustomEvent('eplib-set-word-counts', {});
      this.actionBarComponent.dispatchEvent(wordCountEvent);

      const navPreviousEvent = new CustomEvent('eplib-set-navigation-button-state', {
        detail: {
          direction: 'previous',
          link: data.previous,
          pattern: pattern,
          match: matchParam
        }
      });
      this.actionBarComponent.dispatchEvent(navPreviousEvent);

      const navNextEvent = new CustomEvent('eplib-set-navigation-button-state', {
        detail: {
          direction: 'next',
          link: data.next,
          pattern: pattern,
          match: matchParam
        }
      });
      this.actionBarComponent.dispatchEvent(navNextEvent);

      // for highlighting search hits
      if (pattern) {
        if (this.highlighterComponent) {
          const event = new CustomEvent('eplib-kwic-load-matches', {
            detail: {
              doc: doc.substring(0, doc.lastIndexOf('.')), // strip .xml
              matchParam: matchParam,
              pattern: pattern
            }
          });
          this.highlighterComponent.dispatchEvent(event);
        }
      } // pattern
    } // actionBarComponent


    if (this.historySupport) {
      let historyParams = qparams;
      historyParams.delete('doc');
      let historyUrl = new URL(window.location.href);
      historyUrl.search = historyParams.size > 0 ? historyParams.toString() : '';
      history.pushState(null, null, historyUrl);
    }
  }

  _initContent() {
    const host = this.shadowRoot.host;
    host.querySelectorAll('.content .note').forEach(el =>  {
      new bootstrap.Popover(el, {
        html: true,
        trigger: 'hover',
        container: 'annotatable-document'
      })
    });
  }

  _initTooltip() {
    const content = this.shadowRoot.querySelector('.content');

    if (content) {
      content.querySelectorAll('tei-w').forEach(el =>  {
        new bootstrap.Tooltip(el, {
          delay: 500,
          html: true,
          container: content,
          title: function (el) {
            const lemma = el.getAttribute('lemma') || 'N/A';
            const reg = el.getAttribute('reg') || el.innerHTML;
            const pos = el.getAttribute('pos') || 'N/A';
            return `<div>
                      <span>id: </span><span>${el.id}</span><br/>
                      <span>lemma: </span><span>${lemma}</span><br/>
                      <span>reg: </span><span>${reg}</span><br/>
                      <span>pos: </span><span>${pos}</span>
                    </div>`;
          }
        })
      });
    }
  }

  _enableDisableAnnotations(event) {
    const annotationsEnabled = event.detail.enable_annotations;
    let docs = [].slice.call(this.shadowRoot.querySelectorAll('annotatable-document'));
    docs.forEach(doc => {
      doc.highlight = annotationsEnabled;
    });
  }

  render() {
    return html`
      <link rel="stylesheet" type="text/css" href="../transform/nuteisimple-annotate.css"/>

      <div class="loading ${!this.loading ? ' visually-hidden' : ''}">
        <span></span>
      </div>
      <annotatable-document panel="annotation-panel" highlight="highlight" id="" source="" version>
          <div slot="document">
            <div class="content"/>
          </div>
      </annotatable-document>
    `;
  }

  static get styles() {
    return [
      BootstrapCSS,
      EplibStyles
    ];
  }

}
customElements.define('eplib-document-content', EplibDocumentContent);